import { useState } from 'react';
import { toast } from 'sonner';
import { ERRORS } from '@/constants';
import { ApiError } from '@/services/api/client';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Panel } from './parts';
import { useChangePassword } from './queries';

const MIN_LENGTH = 12;

/**
 * Profile > Change password.
 *
 * Where the seat_assigned and portal_password_issued emails send somebody who
 * was handed a password. The current password is checked like the sign-in
 * form, so a wrong one counts towards the lockout. On success every other
 * device is signed out and this browser stays in.
 */
export function ChangePasswordCard() {
  const change = useChangePassword();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [tried, setTried] = useState(false);

  const tooShort = next.length > 0 && next.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== next;
  const same = next.length > 0 && next === current;
  const ready = current.length > 0 && next.length >= MIN_LENGTH && confirm === next && !same;

  const apiError = change.error instanceof ApiError ? change.error : null;
  const serverMessage = !apiError
    ? null
    : apiError.code === 'password_incorrect'
      ? 'Your current password is not correct.'
      : apiError.code === 'account_locked'
        ? ERRORS.accountLocked
        : apiError.code === 'rate_limited'
          ? ERRORS.rateLimited
          : (apiError.fieldErrors?.newPassword?.[0] ?? apiError.message);

  const fieldClass = 'h-[42px] rounded-[9px] border-[1.5px] px-[13px]';

  return (
    <Panel
      title="Change password"
      description="Signs you out on every other device. You stay signed in here."
    >
      <form
        noValidate
        className="grid max-w-[420px] gap-4 px-[26px] py-[22px]"
        onSubmit={(event) => {
          event.preventDefault();
          setTried(true);
          if (!ready) return;
          change.mutate(
            { currentPassword: current, newPassword: next },
            {
              onSuccess: ({ sessionsEnded }) => {
                setCurrent('');
                setNext('');
                setConfirm('');
                setTried(false);
                toast.success(
                  sessionsEnded > 0
                    ? `Password changed. Signed out ${String(sessionsEnded)} other ${
                        sessionsEnded === 1 ? 'device' : 'devices'
                      }.`
                    : 'Password changed.',
                );
              },
              onError: () => {
                setCurrent('');
              },
            },
          );
        }}
      >
        {serverMessage && (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {serverMessage}
          </p>
        )}

        <Field id="current-password" label="Current password">
          <Input
            id="current-password"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(event) => {
              setCurrent(event.target.value);
            }}
            aria-invalid={tried && !current}
            className={fieldClass}
          />
        </Field>

        <Field
          id="new-password"
          label="New password"
          hint={
            tooShort
              ? `Use at least ${String(MIN_LENGTH)} characters.`
              : same
                ? 'Choose a password different from the current one.'
                : `At least ${String(MIN_LENGTH)} characters.`
          }
          invalid={tooShort || same || (tried && next.length < MIN_LENGTH)}
        >
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(event) => {
              setNext(event.target.value);
            }}
            aria-invalid={tooShort || same}
            className={fieldClass}
          />
        </Field>

        <Field
          id="confirm-password"
          label="Confirm new password"
          hint={mismatch ? 'The passwords do not match.' : undefined}
          invalid={mismatch}
        >
          <Input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => {
              setConfirm(event.target.value);
            }}
            aria-invalid={mismatch}
            className={fieldClass}
          />
        </Field>

        <div>
          <Button type="submit" disabled={!ready || change.isPending}>
            {change.isPending ? 'Changing…' : 'Change password'}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function Field({
  id,
  label,
  hint,
  invalid,
  children,
}: {
  id: string;
  label: string;
  hint?: string | undefined;
  invalid?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-semibold text-[#48536A]">
        {label}
      </Label>
      {children}
      {hint && (
        <p className={invalid ? 'text-[11px] text-destructive' : 'text-[11px] text-[#8A94A6]'}>
          {hint}
        </p>
      )}
    </div>
  );
}
